import { useMemo } from 'react';
import { Base64 } from 'js-base64';
import { ApolloQueryResult } from '@apollo/client';
import {
  OrgAnalyticsDimensionType,
  OrgAnalyticsLabelValueFilterInput,
  OrgAnalyticsContext,
  useOrganizationAnalyticsQuery,
  OrganizationAnalyticsQuery
} from 'graphql/types';
import { useSupervisorInsightContext } from '../SupervisorInsightContextProvider';

export const encodeDataId = ({
  dimensionValue,
  contextValue,
  labels,
  dateRangeValue,
  pageNumber,
  labelId
}: {
  dimensionValue: OrgAnalyticsDimensionType;
  contextValue: OrgAnalyticsContext;
  labels: OrgAnalyticsLabelValueFilterInput[];
  dateRangeValue: {
    startDate: string;
    endDate: string;
  };
  pageNumber?: number;
  labelId?: string;
}): string => {
  const input = {
    context: contextValue,
    dimensionType: dimensionValue,
    dateRange: {
      fromDate: dateRangeValue.startDate,
      toDate: dateRangeValue.endDate
    },
    filterBylabels: labels,
    pageNumber: pageNumber || 1,
    labelId: labelId
  };
  const stringInput = JSON.stringify(input);
  const id = Base64.encode(stringInput);
  return id;
};

export const useGetSupervisorAnalyticsData = (
  dimension: OrgAnalyticsDimensionType,
  labels: OrgAnalyticsLabelValueFilterInput[],
  context: OrgAnalyticsContext,
  labelId?: string
): {
  data: OrganizationAnalyticsQuery | undefined;
  loading: boolean;
  isEmpty: boolean;
  encodedId: string;
  refetch: (
    variables?: any
  ) => Promise<ApolloQueryResult<OrganizationAnalyticsQuery>>;
} => {
  const { currentPeriod } = useSupervisorInsightContext();
  const { startDate, endDate } = currentPeriod ?? {};
  const encodeId = encodeDataId({
    dimensionValue: dimension,
    contextValue: context,
    labels: labels,
    dateRangeValue: {
      startDate: startDate || '',
      endDate: endDate || ''
    },
    labelId: labelId
  });

  const { data, loading, refetch } = useOrganizationAnalyticsQuery({
    variables: {
      id: encodeId
    }
  });

  return useMemo(
    () => ({
      data: data,
      loading: loading,
      isEmpty: Boolean(data?.orgAnalytics?.analyticsResult.items.length === 0),
      refetch: refetch,
      encodedId: encodeId
    }),
    [data, encodeId, loading, refetch]
  );
};
